// Renaming a place (4.10.55, 2026-10-26). The schedule's events and the
// itinerary page kept the old name — after renaming a journal place called
// "PXL_20261014…" the itinerary still showed the file name. Now:
//  - events linked to the place whose title is EXACTLY the old name are
//    renamed too (an edited or multi-place title is left alone);
//  - the itinerary page is never changed by itself: the app asks, and
//    "Replace the name" swaps the old name for the new one in its text only,
//    keeping everything else the person wrote.
// Kept identical in andiamo-web and andiamo-places (src/Common).

const placeIds = (ev) => String(ev?.Places || "").split(",").map((s) => s.trim()).filter(Boolean);

// Renames the matching events in place; returns how many.
export function renameLinkedEvents(events, placeId, oldName, newName) {
  const from = String(oldName || "").trim();
  const to = String(newName || "").trim();
  if (!from || !to || from === to) return 0;
  let count = 0;
  (events || []).forEach((ev) => {
    if (String(ev.Subject || "").trim() === from && placeIds(ev).includes(String(placeId))) {
      ev.Subject = to;
      count++;
    }
  });
  return count;
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// The name as a whole word (not inside a longer word), in either its plain
// or HTML-escaped form.
const nameRe = (name) => {
  const forms = [name, name.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")];
  const alt = Array.from(new Set(forms)).map(escapeRe).join("|");
  return new RegExp(`(^|[^\\p{L}\\p{N}_])(${alt})(?=$|[^\\p{L}\\p{N}_])`, "gu");
};

// Applies fn to the text between tags only — never to tags or attributes
// (links, image addresses).
function mapText(html, fn) {
  return String(html || "")
    .split(/(<[^>]*>)/)
    .map((part) => (part.startsWith("<") ? part : fn(part)))
    .join("");
}

export function countNameInHtml(html, name) {
  const n = String(name || "").trim();
  if (!n) return 0;
  let count = 0;
  mapText(html, (text) => {
    count += (text.match(nameRe(n)) || []).length;
    return text;
  });
  return count;
}

// { html, count } with every whole-word occurrence of oldName in the text
// replaced by newName.
export function replaceNameInHtml(html, oldName, newName) {
  const from = String(oldName || "").trim();
  const to = String(newName || "").trim();
  if (!from || !to || from === to) return { html: html || "", count: 0 };
  const safeTo = to.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let count = 0;
  const out = mapText(html, (text) =>
    text.replace(nameRe(from), (m, pre) => {
      count++;
      return pre + safeTo;
    })
  );
  return { html: out, count };
}
